import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { FaRegImage, FaMagic } from 'react-icons/fa';
import 'react-toastify/dist/ReactToastify.css';
import { FaBullseye } from 'react-icons/fa';
import { FaCreditCard, FaBriefcase, FaHome } from 'react-icons/fa';
import { fetchAudienceEstimate } from '../../helpers/adFunctions';
import { CTA_MATRIX, OBJECTIVE_MATRIX, deliveryFor, selectedPlatformKeys, uiPlatformKey } from '../../helpers/platformAdCapabilities';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { FaHeart } from 'react-icons/fa';
import {
  FaRoad, FaShoppingCart, FaMobileAlt, FaClipboardList,
  FaBroadcastTower, FaBullhorn, FaThumbsUp,
  FaFacebook, FaInstagram, FaTiktok, FaGoogle,
  FaSnapchatGhost, FaLinkedin, FaShopify
} from 'react-icons/fa';
import { FaIdCard, FaMapMarkerAlt, FaUsers, FaFilter, FaCalendarAlt, FaClock, FaBoxOpen } from 'react-icons/fa';
import { FaFlag, FaWallet, FaPenNib, FaEye } from 'react-icons/fa';
import { getRequest, postRequest } from '../../services/api/apiService';
import { useCreateAdContext } from '../../context/CreateAdContext';

const locationOptions = [
  { label: 'North Africa', options: [
    { value: 'MA', label: 'Morocco' },
    { value: 'EG', label: 'Egypt' },
    { value: 'DZ', label: 'Algeria' },
    { value: 'TN', label: 'Tunisia' },
    { value: 'LY', label: 'Libya' },
  ]},
  { label: 'Middle East', options: [
    { value: 'SA', label: 'Saudi Arabia' },
    { value: 'AE', label: 'UAE' },
    { value: 'QA', label: 'Qatar' },
    { value: 'KW', label: 'Kuwait' },
    { value: 'BH', label: 'Bahrain' },
    { value: 'OM', label: 'Oman' },
    { value: 'JO', label: 'Jordan' },
    { value: 'LB', label: 'Lebanon' },
    { value: 'IQ', label: 'Iraq' },
  ]},
  { label: 'West Africa', options: [
    { value: 'SN', label: 'Senegal' },
    { value: 'CI', label: "Côte d'Ivoire" },
    { value: 'GH', label: 'Ghana' },
    { value: 'NG', label: 'Nigeria' },
    { value: 'CM', label: 'Cameroon' },
    { value: 'TG', label: 'Togo' },
    { value: 'BJ', label: 'Benin' },
    { value: 'ML', label: 'Mali' },
  ]},
  { label: 'Europe', options: [
    { value: 'FR', label: 'France' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'DE', label: 'Germany' },
    { value: 'ES', label: 'Spain' },
    { value: 'IT', label: 'Italy' },
    { value: 'NL', label: 'Netherlands' },
    { value: 'BE', label: 'Belgium' },
    { value: 'PT', label: 'Portugal' },
    { value: 'CH', label: 'Switzerland' },
    { value: 'SE', label: 'Sweden' },
    { value: 'NO', label: 'Norway' },
    { value: 'DK', label: 'Denmark' },
    { value: 'PL', label: 'Poland' },
    { value: 'TR', label: 'Turkey' },
    { value: 'AT', label: 'Austria' },
    { value: 'IE', label: 'Ireland' },
  ]},
  { label: 'Americas', options: [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'MX', label: 'Mexico' },
    { value: 'BR', label: 'Brazil' },
    { value: 'AR', label: 'Argentina' },
    { value: 'CO', label: 'Colombia' },
    { value: 'CL', label: 'Chile' },
  ]},
  { label: 'Asia-Pacific', options: [
    { value: 'AU', label: 'Australia' },
    { value: 'NZ', label: 'New Zealand' },
    { value: 'IN', label: 'India' },
    { value: 'JP', label: 'Japan' },
    { value: 'SG', label: 'Singapore' },
    { value: 'ID', label: 'Indonesia' },
    { value: 'MY', label: 'Malaysia' },
    { value: 'PH', label: 'Philippines' },
    { value: 'TH', label: 'Thailand' },
    { value: 'KR', label: 'South Korea' },
    { value: 'HK', label: 'Hong Kong' },
  ]},
];

const moroccoCitiesOptions = [
  { value: '2553604', label: 'Casablanca' },
  { value: '2537763', label: 'Rabat' },
  { value: '2542978', label: 'Marrakech' },
  { value: '2548885', label: 'Fes' },
  { value: '2527087', label: 'Tangier' },
  { value: '2566226', label: 'Agadir' },
  { value: '2542601', label: 'Meknes' },
  { value: '2529177', label: 'Oujda' },
  { value: '2543831', label: 'Kenitra' },
  { value: '2527084', label: 'Tetouan' },
  { value: '2548453', label: 'El Jadida' },
  { value: '2542309', label: 'Mohammedia' },
  { value: '2537775', label: 'Safi' },
  { value: '2550467', label: 'Béni Mellal' },
  { value: '2542332', label: 'Nador' },
];

const languageOptions = [
  { value: 'ar', label: 'Arabic' },
  { value: 'fr', label: 'French' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'nl', label: 'Dutch' },
  { value: 'tr', label: 'Turkish' },
  { value: 'wo', label: 'Wolof' },
  { value: 'ha', label: 'Hausa' },
  { value: 'id', label: 'Indonesian' },
  { value: 'ms', label: 'Malay' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'zh', label: 'Chinese (Simplified)' },
  { value: 'hi', label: 'Hindi' },
];

// ── Static options (placements, LinkedIn) ──────────────────────────────────────

const metaPlacementOptions = [
  { label: 'Facebook', options: [
    { value: 'feed', label: 'Facebook Feed' },
    { value: 'right_hand_column', label: 'Facebook Right Column' },
    { value: 'marketplace', label: 'Facebook Marketplace' },
    { value: 'video_feeds', label: 'Facebook Video Feed' },
    { value: 'story', label: 'Facebook Stories' },
    { value: 'search', label: 'Facebook Search' },
    { value: 'reels', label: 'Facebook Reels' },
  ]},
  { label: 'Instagram', options: [
    { value: 'instagram_feed', label: 'Instagram Feed' },
    { value: 'instagram_story', label: 'Instagram Stories' },
    { value: 'instagram_explore', label: 'Instagram Explore' },
    { value: 'instagram_reels', label: 'Instagram Reels' },
    { value: 'instagram_shop', label: 'Instagram Shop' },
  ]},
  { label: 'Audience Network', options: [
    { value: 'an_classic', label: 'Audience Network' },
    { value: 'an_interstitial', label: 'Audience Network Interstitial' },
  ]},
  { label: 'Messenger', options: [
    { value: 'messenger_inbox', label: 'Messenger Inbox' },
    { value: 'messenger_stories', label: 'Messenger Stories' },
  ]},
];

const linkedinSeniorityOptions = [
  { value: '1', label: 'Unpaid' },
  { value: '2', label: 'Training' },
  { value: '3', label: 'Entry' },
  { value: '4', label: 'Senior' },
  { value: '5', label: 'Manager' },
  { value: '6', label: 'Director' },
  { value: '7', label: 'VP' },
  { value: '8', label: 'CXO' },
  { value: '9', label: 'Partner' },
  { value: '10', label: 'Owner' },
];

const linkedinIndustryOptions = [
  { value: '1', label: 'Technology & Software' },
  { value: '3', label: 'Finance' },
  { value: '4', label: 'Education' },
  { value: '6', label: 'Healthcare' },
  { value: '7', label: 'Manufacturing' },
  { value: '11', label: 'Retail' },
  { value: '12', label: 'Telecommunications' },
  { value: '14', label: 'Media & Entertainment' },
  { value: '16', label: 'Real Estate' },
  { value: '19', label: 'Marketing & Advertising' },
  { value: '26', label: 'E-commerce' },
  { value: '27', label: 'Food & Beverages' },
  { value: '28', label: 'Transportation & Logistics' },
  { value: '30', label: 'Legal Services' },
  { value: '31', label: 'Consulting' },
];

const linkedinJobFunctionOptions = [
  { value: '1', label: 'Accounting' },
  { value: '2', label: 'Administrative' },
  { value: '3', label: 'Arts & Design' },
  { value: '4', label: 'Business Development' },
  { value: '5', label: 'Community & Social Services' },
  { value: '6', label: 'Consulting' },
  { value: '7', label: 'Education' },
  { value: '8', label: 'Engineering' },
  { value: '9', label: 'Entrepreneurship' },
  { value: '10', label: 'Finance' },
  { value: '11', label: 'Healthcare Services' },
  { value: '12', label: 'Human Resources' },
  { value: '13', label: 'Information Technology' },
  { value: '14', label: 'Legal' },
  { value: '15', label: 'Marketing' },
  { value: '16', label: 'Media & Communications' },
  { value: '17', label: 'Military & Protective Services' },
  { value: '18', label: 'Operations' },
  { value: '19', label: 'Product Management' },
  { value: '20', label: 'Program & Project Management' },
  { value: '21', label: 'Purchasing' },
  { value: '22', label: 'Quality Assurance' },
  { value: '23', label: 'Real Estate' },
  { value: '24', label: 'Research' },
  { value: '25', label: 'Sales' },
];

const linkedinCompanySizeOptions = [
  { value: 'A', label: 'Self-employed' },
  { value: 'B', label: '1-10 employees' },
  { value: 'C', label: '11-50 employees' },
  { value: 'D', label: '51-200 employees' },
  { value: 'E', label: '201-500 employees' },
  { value: 'F', label: '501-1000 employees' },
  { value: 'G', label: '1001-5000 employees' },
  { value: 'H', label: '5001-10000 employees' },
  { value: 'I', label: '10001+ employees' },
];

const tiktokInterestOptions = [
  { value: '23|2', label: 'Apparel & Accessories' },
  { value: '23|3', label: 'Auto & Vehicles' },
  { value: '23|4', label: 'Baby, Kids & Maternity' },
  { value: '23|5', label: 'Beauty & Personal Care' },
  { value: '23|6', label: 'Books & Literature' },
  { value: '23|7', label: 'Business & Finance' },
  { value: '23|8', label: 'Computers & Electronics' },
  { value: '23|9', label: 'Education' },
  { value: '23|10', label: 'Entertainment' },
  { value: '23|11', label: 'Food & Beverage' },
  { value: '23|13', label: 'Gaming' },
  { value: '23|14', label: 'Health & Fitness' },
  { value: '23|15', label: 'Home & Garden' },
  { value: '23|16', label: 'Music' },
  { value: '23|17', label: 'News & Politics' },
  { value: '23|18', label: 'Sports & Outdoors' },
  { value: '23|19', label: 'Travel' },
  { value: '23|20', label: 'Pets & Animals' },
  { value: '23|21', label: 'Photography' },
  { value: '23|22', label: 'Real Estate' },
  { value: '23|23', label: 'Religion & Spirituality' },
  { value: '23|24', label: 'Science & Nature' },
  { value: '23|25', label: 'Shopping' },
  { value: '23|26', label: 'Sports & Fitness' },
];

// ── En-tête d'étape de l'accordéon 3 étapes ───────────────────────
// Création guidée : 1. Campagne → 2. Audience → 3. Créatif & lancement.
// Une seule étape ouverte à la fois ; pastille numérotée qui devient ✓ verte
// quand les champs clés de l'étape sont remplis. Cliquer un en-tête ouvre
// l'étape (navigation libre en avant/arrière).
const StepHeader = ({ n, title, subtitle, open, done, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full flex items-center gap-3 text-left rounded-2xl px-4 py-3 mb-2 border transition ${
      open ? 'bg-[#221C51] border-[#221C51] shadow-md' : 'bg-white border-gray-200 hover:border-[#221C51]/40'
    }`}
  >
    <span
      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0 ${
        open ? 'bg-white text-[#221C51]' : done ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {done && !open ? '✓' : n}
    </span>
    <div className="min-w-0 flex-1">
      <h3 className={`text-base md:text-lg font-bold leading-tight ${open ? 'text-white' : 'text-[#221C51]'}`}>{title}</h3>
      <p className={`text-[11px] md:text-xs ${open ? 'text-purple-200' : done ? 'text-emerald-600 font-medium' : 'text-gray-400'}`}>
        {done && !open ? 'Complété ✓' : subtitle}
      </p>
    </div>
    <span className={`text-sm transition-transform ${open ? 'rotate-180 text-white' : 'text-gray-400'}`}>▾</span>
  </button>
);

const CreateAd = ({ isPremium, shopifyProduct, initialPlatform, aiMode = false, onSwitchToAI, onSwitchToManual, hideHeader = false }) => {
  const navigate = useNavigate();
  const { pendingActions, clearActions, submitSignal, isSimpleCampaign, setIsSimpleCampaign } = useCreateAdContext();
  const [highlightedFields, setHighlightedFields] = useState(new Set());
  const [showCampaign, setShowCampaign] = useState(true);
  const [showAdset, setShowAdset] = useState(false);
  const [showAd, setShowAd] = useState(false);
  const [promotedObjectFields, setPromotedObjectFields] = useState([]);
  const [metaData, setMetaData] = useState({ businessId: '', pages: [], apps: [], pixels: [], instagramProfiles: [] });
  const [metaAdAccounts, setMetaAdAccounts] = useState([]);
  const [tiktokAdAccounts, setTiktokAdAccounts] = useState([]);
  const [tiktokIdentities, setTiktokIdentities] = useState([]);
  const [tiktokPixels, setTiktokPixels] = useState([]);
  const [tiktokVideos, setTiktokVideos] = useState([]);
  const [tiktokApps, setTiktokApps] = useState([]);
  const [linkedInAdAccounts, setLinkedInAdAccounts] = useState([]);
  const [snapchatAdAccounts, setSnapchatAdAccounts] = useState([]);
  const [googleAdAccounts, setGoogleAdAccounts] = useState([]);
  const [selectedAccessToken, setSelectedAccessToken] = useState('');
  const [audienceEstimate, setAudienceEstimate] = useState(null);
  const [selectedAdAccount, setSelectedAdAccount] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [tiktokInterestCategories, setTiktokInterestCategories] = useState(tiktokInterestOptions);

  // ── Async loader for Meta Interests (calls backend → Graph API search) ──────
  const loadMetaInterests = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return [];
    try {
      const res = await getRequest(`/api/ads/meta/interests?q=${encodeURIComponent(inputValue)}`);
      return (res?.data || []).map((i) => ({
        value: i.id,
        label: i.audience_size
          ? `${i.name} (~${Number(i.audience_size).toLocaleString()} people)`
          : i.name,
        id: i.id,
        name: i.name,
      }));
    } catch {
      return [];
    }
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    objective: 'OUTCOME_TRAFFIC',
    status: 'PAUSED',
    category: [],
    publisherPlatform: [],
    totalBudget: 0,
    smartBudget: 0,
    dayparting: 'All Day',
    specificTimes: '',
    gender: 'All',
    optimization_goal: 'LINK_CLICKS',
    age: [],
    languages: [],
    location: ['MA'],
    cities: [],
    adSetName: '',
    billingEvent: 'LINK_CLICKS',
    spendingPower: 'Low',
    startTime: '',
    endTime: '',
    frequency_cap: 0,
    frequency_cap_reset_period: 0,
    frequency_cap_time_unit: 'DAY',
    creativeType: 'Image',
    adCreativeName: '',
    imageUrl: '',
    videoUrl: '',
    videoThumbnailUrl: '',
    adTitle: '',
    adText: '',
    description: '',
    callToAction: '',
    destinationUrl: '',
    budgetTypeMeta: 'Daily',
    currencyMeta: 'MAD',
    budgetMeta: 0,
    budgetTypeTikTok: 'Daily',
    currencyTikTok: 'MAD',
    budgetTikTok: 0,
    budgetTypeGoogle: 'Daily',
    currencyGoogle: 'MAD',
    budgetGoogle: 0,
    budgetTypeSnapchat: 'Daily',
    currencySnapchat: 'MAD',
    budgetSnapchat: 0,
    budgetTypeLinkedIn: 'Daily',
    currencyLinkedIn: 'MAD',
    budgetLinkedIn: 0,
    budgetTypeShopify: 'Daily',
    currencyShopify: 'MAD',
    budgetShopify: 0,
    // Interests (Meta + TikTok)
    interests: [],
    // Placements
    placements: [],
    // LinkedIn-specific targeting
    linkedinSeniorities: [],
    linkedinIndustries: [],
    linkedinJobFunctions: [],
    linkedinCompanySizes: [],
    // Bid strategy + placement
    bid_strategy: 'LOWEST_COST',
    tiktokPlacement: 'PLACEMENT_TYPE_AUTOMATIC',
  });

  const [aiPrompt, setAiPrompt] = useState('');
  const [aiAspectRatio, setAiAspectRatio] = useState('1:1');
  const [generating, setGenerating] = useState(false);

  // Helper: returns yellow-ring class when a field is highlighted by the AI chatbot
  const hl = (field) =>
    highlightedFields.has(field) ? ' ring-2 ring-yellow-400 ring-offset-1 transition-all' : '';

  // ── Validation croisée plateformes × objectif (matrice adCapabilities) ──
  // Un objectif est indisponible si AU MOINS UNE plateforme sélectionnée ne le
  // supporte pas (ex: App promotion sur LinkedIn/Google) — l'option est alors
  // désactivée dans le sélecteur avec la raison. Symétriquement, une
  // plateforme incompatible avec l'objectif déjà choisi est désactivée.
  const objectiveBlockedOn = (objValue) => {
    const keys = selectedPlatformKeys(formData.publisherPlatform);
    return keys.filter((pk) => OBJECTIVE_MATRIX[objValue]?.[pk]?.value === null);
  };
  const platformBlockedByObjective = (platformLabel) =>
    !!formData.objective && OBJECTIVE_MATRIX[formData.objective]?.[uiPlatformKey(platformLabel)]?.value === null;

  // Choix des plateformes : si l'objectif courant devient indisponible sur la
  // nouvelle sélection, on le vide (avec explication) plutôt que d'envoyer une
  // combinaison que l'API rejettera.
  const handlePlatformsChange = (selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map((o) => o.value) : [];
    setFormData((prev) => {
      const next = { ...prev, publisherPlatform: values };
      if (prev.objective) {
        const blocked = [...new Set(values.map(uiPlatformKey))].filter(
          (pk) => OBJECTIVE_MATRIX[prev.objective]?.[pk]?.value === null
        );
        if (blocked.length > 0) {
          next.objective = '';
          toast.warn(`Objectif réinitialisé : indisponible sur ${blocked.join(', ')}. Choisissez un objectif compatible.`);
        }
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, publisherPlatform: values.length === 0 }));
  };

  // Choix de l'objectif : retire automatiquement les plateformes sélectionnées
  // qui ne le supportent pas (avec explication).
  const handleObjectiveChange = (sel) => {
    const objective = sel ? sel.value : '';
    setFormData((prev) => {
      const incompatible = prev.publisherPlatform.filter(
        (label) => OBJECTIVE_MATRIX[objective]?.[uiPlatformKey(label)]?.value === null
      );
      if (incompatible.length > 0) {
        toast.warn(`${incompatible.join(' et ')} retiré(es) : objectif indisponible sur cette plateforme.`);
        return { ...prev, objective, publisherPlatform: prev.publisherPlatform.filter((l) => !incompatible.includes(l)) };
      }
      return { ...prev, objective };
    });
    if (objective) setErrors((prev) => ({ ...prev, objective: false }));
  };

  // État de complétion de chaque étape de l'accordéon (champs clés remplis) —
  // pilote les pastilles ✓ des en-têtes StepHeader.
  const stepDone = {
    campaign: !!(formData.name && formData.publisherPlatform.length > 0 && formData.objective),
    adset: !!(formData.adSetName && formData.startTime && (formData.location || []).length > 0),
    ad: !!(formData.adCreativeName && formData.adTitle && formData.adText && formData.callToAction && formData.destinationUrl),
  };

  // Apply actions dispatched by AiChatBot — stagger fields, highlight each briefly
  useEffect(() => {
    if (!pendingActions || pendingActions.length === 0) return;

    const SECTION_FIELDS = {
      campaign: ['name', 'publisherPlatform', 'objective', 'status'],
      adset: ['location', 'gender', 'age', 'languages', 'adSetName', 'startTime', 'endTime',
              'budgetMeta', 'budgetTikTok', 'budgetGoogle', 'budgetSnapchat', 'budgetLinkedIn'],
      ad: ['adCreativeName', 'creativeType', 'adTitle', 'adText', 'description', 'callToAction', 'destinationUrl'],
    };

    // eslint-disable-next-line no-console
    console.log('[Alex] applique', pendingActions.length, 'action(s) sur le formulaire :', pendingActions);

    pendingActions.forEach((action, idx) => {
      setTimeout(() => {
        setFormData((prev) => {
          const updated = { ...prev };
          if (Array.isArray(action.value) && Array.isArray(prev[action.field])) {
            updated[action.field] = action.value;
          } else {
            updated[action.field] = action.value;
          }
          // eslint-disable-next-line no-console
          console.log(`[Alex] champ "${action.field}" :`, prev[action.field], '→', action.value);
          return updated;
        });

        // Open the relevant section
        if (SECTION_FIELDS.campaign.includes(action.field)) setShowCampaign(true);
        if (SECTION_FIELDS.adset.includes(action.field)) setShowAdset(true);
        if (SECTION_FIELDS.ad.includes(action.field)) setShowAd(true);

        // Yellow highlight for 1.5 s
        setHighlightedFields((prev) => new Set([...prev, action.field]));
        setTimeout(() => {
          setHighlightedFields((prev) => {
            const next = new Set(prev);
            next.delete(action.field);
            return next;
          });
        }, 1500);
      }, idx * 200);
    });

    clearActions();
  }, [pendingActions, clearActions]);

  // Triggered by AiChatBot when user confirms campaign launch
  useEffect(() => {
    if (submitSignal > 0) {
      handleSubmit({ preventDefault: () => {} });
    }
  }, [submitSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load real TikTok interest categories from the API when TikTok is selected
  useEffect(() => {
    if (formData.publisherPlatform.includes('TikTok')) {
      getRequest('/api/ads/tiktok/interest-categories')
        .then((res) => {
          if (res?.data?.length) {
            setTiktokInterestCategories(res.data.map((i) => ({ value: i.id, label: i.name })));
          }
        })
        .catch(() => {}); // silently fall back to static list
    }
  }, [formData.publisherPlatform]);

  useEffect(() => { calculateTotalBudget(); }, [formData.budgetMeta, formData.budgetTikTok, formData.budgetGoogle, formData.budgetSnapchat, formData.budgetLinkedIn, formData.budgetShopify]);

  // Pre-fill from Shopify product
  useEffect(() => {
    if (!shopifyProduct) return;
    setFormData((prev) => ({
      ...prev,
      name: shopifyProduct.title || prev.name,
      adCreativeName: shopifyProduct.title || prev.adCreativeName,
      adTitle: shopifyProduct.title || prev.adTitle,
      adText: shopifyProduct.description || prev.adText,
      description: shopifyProduct.description || prev.description,
      destinationUrl: shopifyProduct.product_url || prev.destinationUrl,
      imageUrl: shopifyProduct.image_url || prev.imageUrl,
      creativeType: shopifyProduct.image_url ? 'Image' : prev.creativeType,
      callToAction: prev.callToAction || 'SHOP_NOW',
      ...(initialPlatform ? { publisherPlatform: [initialPlatform] } : {}),
    }));
    setShowAd(true);
  }, [shopifyProduct]);

  useEffect(() => {
    if (formData.publisherPlatform.includes('Facebook') || formData.publisherPlatform.includes('Instagram')) {
      fetchMetaAdAccounts();
    }
    if (formData.publisherPlatform.includes('TikTok')) {
      fetchTiktokAdAccounts();
      fetchTiktokIdentitiesList();
    }
    if (formData.publisherPlatform.includes('LinkedIn')) {
      fetchLinkedInAdAccounts();
    }
    if (formData.publisherPlatform.includes('Snapchat')) {
      fetchSnapchatAdAccounts();
    }
    if (formData.publisherPlatform.includes('Google')) {
      fetchGoogleAdAccounts();
    }
  }, [formData.publisherPlatform]);

  useEffect(() => {
    fetchAudienceEstimate(formData, selectedAdAccount, setAudienceEstimate);
  }, [formData.publisherPlatform, formData.location, formData.age, formData.gender, formData.languages, formData.interests, selectedAdAccount]);

  useEffect(() => {
    if (selectedAdAccount) fetchMetaBusinessData();
  }, [selectedAdAccount]);

  useEffect(() => {
    removeOldPromotedObjectFields();
    generatePromotedObjectFields(formData.objective, formData.publisherPlatform);
  }, [formData.objective, formData.publisherPlatform, metaData]);

  useEffect(() => {
    if (formData.objective === 'OUTCOME_CONVERSIONS' && formData.publisherPlatform.includes('TikTok')) {
      fetchTiktokPixelsList();
    }
    if (formData.objective === 'OUTCOME_APP_PROMOTION' && formData.publisherPlatform.includes('TikTok')) {
      getRequest('/api/ads/tiktok/apps').then(r => setTiktokApps(r?.apps || [])).catch(() => setTiktokApps([]));
    }
  }, [formData.objective, formData.publisherPlatform]);

  useEffect(() => {
    if (formData.publisherPlatform.includes('TikTok') && formData.creativeType === 'Video' && formData.identityId && formData.identityType) {
      fetchTiktokVideosList(formData.identityId, formData.identityType);
    }
  }, [formData.publisherPlatform, formData.creativeType, formData.identityId, formData.identityType]);

  const removeOldPromotedObjectFields = () => {
    const updatedFormData = { ...formData };
    promotedObjectFields.forEach(field => { delete updatedFormData[field.name]; });
    setFormData(updatedFormData);
  };

  const calculateTotalBudget = () => {
    const total = (parseFloat(formData.budgetMeta) || 0) + (parseFloat(formData.budgetTikTok) || 0) + (parseFloat(formData.budgetGoogle) || 0) + (parseFloat(formData.budgetSnapchat) || 0) + (parseFloat(formData.budgetLinkedIn) || 0) + (parseFloat(formData.budgetShopify) || 0);
    setFormData(prev => ({ ...prev, totalBudget: total, smartBudget: total }));
  };

  // ── FIXED: calls the real backend endpoint ──
  const fetchMetaAdAccounts = async () => {
    try {
      const response = await getRequest('/api/ads/meta/ad-accounts');
      if (response?.connected && response?.adAccounts?.length) {
        const accounts = response.adAccounts.map(acc => ({
          value: acc.adAccountId,
          label: `${acc.name} (${acc.adAccountId})`,
          accessToken: acc.accessToken,
        }));
        setMetaAdAccounts(accounts);
        // Auto-select the first account so reach estimate fires immediately
        if (accounts.length > 0) {
          const first = accounts[0];
          setSelectedAdAccount(prev => prev || first.value);
          setFormData(prev => ({ ...prev, adAccountMeta: prev.adAccountMeta || first.value }));
          if (first.accessToken) setSelectedAccessToken(t => t || first.accessToken);
        }
      } else {
        toast.error("No Meta ad accounts found. Make sure Meta is connected.");
      }
    } catch (error) {
      console.error("Error fetching Meta ad accounts:", error);
      toast.error("Failed to load Meta ad accounts. Is Meta connected?");
    }
  };

  const fetchLinkedInAdAccounts = async () => {
    try {
      const response = await getRequest('/api/ads/linkedin/ad-accounts');
      const accounts = response?.data?.accounts || [];
      if (accounts.length) {
        setLinkedInAdAccounts(accounts.map(acc => ({ value: acc.id, label: `${acc.name} (${acc.id})` })));
      } else {
        toast.error("No LinkedIn ad accounts found. Make sure LinkedIn is connected and an account is selected.");
      }
    } catch (error) {
      console.error("Error fetching LinkedIn ad accounts:", error);
      toast.error("Failed to load LinkedIn ad accounts. Is LinkedIn connected?");
    }
  };

  const fetchTiktokAdAccounts = async () => {
    try {
      const response = await getRequest('/api/ads/tiktok/fetch-tiktok-ads-account');
      const ids = response?.data?.advertiser_ids || [];
      if (ids.length) {
        const accounts = ids.map(id => ({ value: id, label: `Advertiser ${id}` }));
        setTiktokAdAccounts(accounts);
        // Auto-select first advertiser
        setFormData(prev => ({ ...prev, adAccountTikTok: prev.adAccountTikTok || accounts[0].value }));
      } else {
        toast.error("No TikTok advertiser IDs found. Make sure TikTok is connected and an advertiser is selected.");
      }
    } catch (error) {
      console.error("Error fetching TikTok ad accounts:", error);
      toast.error("Failed to load TikTok advertiser IDs. Is TikTok connected?");
    }
  };

  // createTiktokCampaign auto-picks the first authorized identity if none is
  // supplied — this lets the user choose among all of them instead.
  const fetchTiktokIdentitiesList = async () => {
    try {
      const response = await getRequest('/api/ads/tiktok/identities');
      const identities = response?.identities || [];
      setTiktokIdentities(identities);
      if (identities.length) {
        setFormData(prev => ({
          ...prev,
          identityId: prev.identityId || identities[0].identityId,
          identityType: prev.identityType || identities[0].identityType,
        }));
      }
    } catch (error) {
      console.error("Error fetching TikTok identities:", error);
    }
  };

  // TikTok exige une source Spark Ads (publication existante) pour la plupart
  // des objectifs vidéo — confirmé en live : sans ça, "The source of this
  // post is invalid." Cette liste vient des vidéos déjà publiées par
  // l'identité connectée ; l'utilisateur peut sinon coller un item ID.
  const fetchTiktokVideosList = async (identityId, identityType) => {
    try {
      const params = identityId && identityType ? `?identityId=${identityId}&identityType=${identityType}` : '';
      const response = await getRequest(`/api/ads/tiktok/videos${params}`);
      setTiktokVideos(response?.videos || []);
    } catch (error) {
      console.error("Error fetching TikTok videos:", error);
      setTiktokVideos([]);
    }
  };

  // Objectif Ventes/Conversions -> TikTok exige un pixel connecté (confirmé
  // en live, sinon "Please select a pixel."). Gérer/créer un pixel : /dashboard/pixels.
  const fetchTiktokPixelsList = async () => {
    try {
      const response = await getRequest('/api/ads/pixels/tiktok');
      setTiktokPixels(response?.pixels || []);
    } catch (error) {
      console.error("Error fetching TikTok pixels:", error);
    }
  };

  const fetchSnapchatAdAccounts = async () => {
    try {
      const response = await getRequest('/api/ads/snapchat/fetch-available-ad-accounts');
      const accounts = response?.data || [];
      if (accounts.length) {
        const mapped = accounts.map(acc => ({ value: acc.id, label: `${acc.name || acc.id} (${acc.id})` }));
        setSnapchatAdAccounts(mapped);
        setSelectedAdAccount(prev => prev || mapped[0].value);
        setFormData(prev => ({ ...prev, adAccountSnapchat: prev.adAccountSnapchat || mapped[0].value }));
      } else {
        toast.error("No Snapchat ad accounts found. Make sure Snapchat is connected.");
      }
    } catch (error) {
      console.error("Error fetching Snapchat ad accounts:", error);
      toast.error("Failed to load Snapchat ad accounts. Is Snapchat connected?");
    }
  };

  const fetchGoogleAdAccounts = async () => {
    try {
      const response = await getRequest('/api/ads/google/fetch-accessible-ads-accounts');
      const accounts = response?.data || [];
      if (accounts.length) {
        setGoogleAdAccounts(accounts.map(acc => ({ value: acc.id, label: `Account ${acc.id}` })));
      } else {
        toast.error("No Google Ads accounts found. Make sure Google is connected and an account is selected.");
      }
    } catch (error) {
      console.error("Error fetching Google ad accounts:", error);
      toast.error("Failed to load Google Ads accounts. Is Google connected?");
    }
  };

  const handleToggleChange = () => {
    if (!isPremium) { toast.error("Upgrade to Pro to access advanced campaign options."); return; }
    setIsSimpleCampaign(!isSimpleCampaign);
  };

  // Accordéon exclusif : ouvrir une étape ferme les deux autres (déroulé
  // guidé 1 → 2 → 3, navigation libre via les en-têtes).
  const openSection = (section) => {
    setShowCampaign(section === 'Campaign');
    setShowAdset(section === 'Adset');
    setShowAd(section === 'Ad');
  };
  const handleToggleSection = (section) => openSection(section);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (value?.toString().trim() !== '') {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const handleMultiSelectChange = (name, selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(o => o.value) : [];
    setFormData({ ...formData, [name]: values });
    setErrors(prev => ({ ...prev, [name]: values.length === 0 }));
  };

  const handleSelectChange = (name, selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(o => o.value) : [];
    setFormData({ ...formData, [name]: values });
  };

  const handleSelectPromtedObjectChange = (name, selectedOption) => {
    setFormData(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : '' }));
  };

  const handleBudgetChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    if (parseFloat(value) > 0) setErrors(prev => ({ ...prev, [name]: false }));
  };

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;
    const uploadPreset = 'd5rr7y5s';
    const cloudName = 'dxtj7sllk';
    const fd = new FormData();
    fd.append('file', file);
    fd.append('upload_preset', uploadPreset);
    const mediaType = type.toLowerCase();
    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/${mediaType === 'videothumbnail' ? 'image' : mediaType}/upload`,
        { method: 'POST', body: fd }
      );
      const data = await response.json();
      if (mediaType === 'image') setFormData(prev => ({ ...prev, imageUrl: data.secure_url }));
      else if (mediaType === 'video') setFormData(prev => ({ ...prev, videoUrl: data.secure_url }));
      else if (mediaType === 'videothumbnail') setFormData(prev => ({ ...prev, videoThumbnailUrl: data.secure_url }));
      setErrors(prev => ({
        ...prev,
        ...(mediaType === 'image' && { imageFile: false }),
        ...(mediaType === 'video' && { videoFile: false }),
        ...(mediaType === 'videothumbnail' && { videoThumbnailFile: false }),
      }));
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('File upload failed. Check Cloudinary config.');
    }
  };

  // ── FIXED: real campaign submission ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name) newErrors.name = true;
    if (!formData.callToAction) newErrors.callToAction = true;
    if (!formData.destinationUrl) newErrors.destinationUrl = true;
    if (!formData.publisherPlatform.length) newErrors.publisherPlatform = true;
    if (!formData.adSetName) newErrors.adSetName = true;
    if (!formData.startTime) newErrors.startTime = true;
    if (!formData.adCreativeName) newErrors.adCreativeName = true;
    if (formData.creativeType === 'Image' && !formData.imageUrl) newErrors.imageFile = true;
    if (formData.creativeType === 'Video' && !formData.videoUrl) newErrors.videoFile = true;
    if (!formData.adTitle) newErrors.adTitle = true;
    if (!formData.adText) newErrors.adText = true;
    if (!formData.description) newErrors.description = true;

    const platforms = formData.publisherPlatform.map(p => p.toLowerCase());
    if ((platforms.includes('facebook') || platforms.includes('instagram')) && !formData.budgetMeta) {
      newErrors.budgetMeta = true;
    }
    if ((platforms.includes('facebook') || platforms.includes('instagram')) && !formData.adAccountMeta) {
      newErrors.adAccountMeta = true;
    }
    if (platforms.includes('tiktok') && !formData.budgetTikTok) {
      newErrors.budgetTikTok = true;
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error('Please fill all required fields.');
      return;
    }

    setSubmitting(true);
    try {
      if (platforms.includes('facebook') || platforms.includes('instagram')) {
        const result = await postRequest('/api/ads/meta/create-campaign', formData);
        if (result?.data) {
          toast.success(`Meta campaign created! Campaign ID: ${result.data.campaignId}`);
        }
      }
      if (platforms.includes('tiktok')) {
        const tiktokResult = await postRequest('/api/ads/tiktok/create-campaign', formData);
        if (tiktokResult?.data) {
          toast.success(`TikTok campaign created! Campaign ID: ${tiktokResult.data.campaignId}`);
        }
      }
      if (platforms.includes('google')) {
        const googleResult = await postRequest('/api/ads/google/create-campaign', formData);
        if (googleResult?.data) {
          toast.success(`Google campaign created! ID: ${googleResult.data.campaignId}`);
        }
      }
      if (platforms.includes('snapchat')) {
        const snapchatResult = await postRequest('/api/ads/snapchat/create-campaign', formData);
        if (snapchatResult?.data) {
          toast.success(`Snapchat campaign created! Campaign ID: ${snapchatResult.data.campaignId}`);
        }
      }
      if (platforms.includes('linkedin')) {
        const linkedInResult = await postRequest('/api/ads/linkedin/create-campaign', formData);
        if (linkedInResult?.data) {
          toast.success(`LinkedIn campaign created! Campaign ID: ${linkedInResult.data.campaignId}`);
        }
      }
      if (platforms.includes('shopify')) {
        toast.info('Shopify campaign creation coming in Phase 2.');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[CreateAd] ✗ échec de création de campagne', {
        status: error?.response?.status,
        body: error?.response?.data,
        url: error?.config?.url,
        payloadSent: error?.config?.data ? JSON.parse(error.config.data) : undefined,
      });
      toast.error(error?.response?.data?.message || 'Failed to create campaign. Check console.');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchMetaBusinessData = async () => {
    // /me/accounts (via notre backend) liste les Pages gérées par l'utilisateur
    // directement — ça marche même sans Business Manager, contrairement à
    // /{businessId}/owned_pages ci-dessous qui exige un Business enregistré et
    // reste vide sinon (cause probable du sélecteur Page vide pour beaucoup
    // de comptes réels).
    try {
      const pagesResponse = await getRequest('/api/ads/meta/pages');
      const pages = (pagesResponse?.pages || []).map(p => ({ value: p.id, label: `${p.name} (${p.id})` }));
      if (pages.length) setMetaData(prev => ({ ...prev, pages }));
    } catch (error) {
      console.error('Error fetching Meta pages:', error);
    }

    // Même logique pour les pixels : /adspixels sur le compte pub sélectionné
    // marche indépendamment d'un Business Manager, contrairement à
    // /{businessId}/adspixels ci-dessous (peut rester vide sinon).
    try {
      const pixelsResponse = await getRequest('/api/ads/pixels/meta');
      const pixels = (pixelsResponse?.pixels || []).map(p => ({ value: p.pixelId, label: `${p.name || p.pixelId} (${p.pixelId})` }));
      if (pixels.length) setMetaData(prev => ({ ...prev, pixels }));
    } catch (error) {
      console.error('Error fetching Meta pixels:', error);
    }

    try {
      const businessResponse = await fetch(
        `https://graph.facebook.com/v20.0/me/businesses?access_token=${selectedAccessToken}`
      );
      const businessData = await businessResponse.json();
      const businessId = businessData.data?.[0]?.id;
      if (!businessId) return;

      const [pagesRes, appsRes, pixelsRes, igRes] = await Promise.all([
        fetch(`https://graph.facebook.com/v20.0/${businessId}/owned_pages?access_token=${selectedAccessToken}`).then(r => r.json()),
        fetch(`https://graph.facebook.com/v20.0/${businessId}/owned_apps?access_token=${selectedAccessToken}`).then(r => r.json()),
        fetch(`https://graph.facebook.com/v20.0/${businessId}/adspixels?access_token=${selectedAccessToken}`).then(r => r.json()),
        fetch(`https://graph.facebook.com/v20.0/${businessId}/instagram_accounts?access_token=${selectedAccessToken}`).then(r => r.json()),
      ]);

      setMetaData(prev => ({
        businessId,
        pages: pagesRes.data?.length ? pagesRes.data.map(p => ({ value: p.id, label: `${p.name} (${p.id})` })) : prev.pages,
        apps: appsRes.data?.map(a => ({ value: a.id, label: `${a.name} (${a.id})` })) || [],
        pixels: pixelsRes.data?.length ? pixelsRes.data.map(p => ({ value: p.id, label: `${p.name} (${p.id})` })) : prev.pixels,
        instagramProfiles: igRes.data?.map(i => ({ value: i.id, label: i.username || i.id })) || [],
      }));
    } catch (error) {
      console.error('Error fetching Meta business data:', error);
    }
  };

  const generatePromotedObjectFields = (objective, platforms) => {
    let fields = [];
    const addField = (name, type, platform, options = []) => {
      if (!fields.some(f => f.name === name && f.platform === platform)) {
        fields.push({ name, type, platform, options });
        setFormData(prev => ({ ...prev, [name]: prev[name] || '' }));
      }
    };
    const isMeta = platforms.includes('Facebook') || platforms.includes('Instagram');
    const isIG = platforms.includes('Instagram');

    // page_id is always required by Meta API for AdCreative creation
    if (isMeta) addField('page_id', 'select', 'Meta', metaData.pages);

    switch (objective) {
      case 'OUTCOME_TRAFFIC':
        if (isMeta) addField('website_url', 'url', 'Meta');
        if (isIG) addField('instagram_actor_id', 'select', 'Meta', metaData.instagramProfiles);
        break;
      case 'OUTCOME_CONVERSIONS':
        if (isMeta) { addField('website_url', 'url', 'Meta'); addField('pixel_id', 'select', 'Meta', metaData.pixels); }
        break;
      case 'OUTCOME_APP_PROMOTION':
        if (isMeta) addField('app_id', 'select', 'Meta', metaData.apps);
        break;
      case 'OUTCOME_AWARENESS':
      case 'OUTCOME_REACH':
        if (isMeta) addField('profile_url', 'url', 'Meta');
        break;
      case 'OUTCOME_LEADS':
        if (isMeta) addField('lead_form', 'text', 'Meta');
        break;
      default: break;
    }
    setPromotedObjectFields(fields);
  };

  const selectStyles = {
    control: (p) => ({ ...p, backgroundColor: '#f3f4f6', borderColor: '#d1d5db' }),
    menu: (p) => ({ ...p, backgroundColor: '#f3f4f6' }),
  };

  // Un budget par plateforme sélectionnée : chaque appel obtient sa PROPRE
  // carte (bordure + fond) plutôt que d'être empilé nu sous le précédent —
  // avec 2+ plateformes sélectionnées (ex: Meta + TikTok), les blocs sans
  // séparation visuelle nette et 3 champs compressés en `w-1/3` (label +
  // valeur) devenaient illisibles sur mobile, les libellés se chevauchant
  // visuellement au premier coup d'œil (repéré via captures d'écran réelles).
  const renderBudgetSection = (platform, budgetTypeName, budgetName, currencyName, adAccountName, adAccounts) => (
    <div key={platform} className="rounded-2xl border border-gray-100 bg-white p-4 md:p-5 shadow-sm">
      <h4 className="flex items-center gap-2 text-sm font-bold text-[#221C51] mb-4">
        <FaWallet className="text-[#221C51]/70" /> Budget &amp; compte — {platform}
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-semibold text-[#221C51]">Budget Type</label>
          <select name={budgetTypeName} value={formData[budgetTypeName] || 'Daily'} onChange={handleInputChange}
            className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100">
            <option value="Daily">Daily</option>
            <option value="Lifetime">Lifetime</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#221C51]">Budget {platform} <span className="text-red-500">*</span></label>
          <input type="number" name={budgetName} placeholder="Enter Budget" value={formData[budgetName] || ''}
            onChange={(e) => handleBudgetChange(budgetName, e.target.value)}
            className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
          {errors[budgetName] && <span className="text-red-500 text-xs">Budget {platform} is required</span>}
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#221C51]">Currency</label>
          <select name={currencyName} value={formData[currencyName] || 'MAD'} onChange={handleInputChange}
            className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100">
            <option value="MAD">MAD</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </select>
        </div>
      </div>
      <div className="mt-4">
        <label className="block text-sm font-semibold text-[#221C51]">{platform} Ad Account <span className="text-red-500">*</span></label>
        <Select
          name={adAccountName}
          options={adAccounts}
          value={adAccounts.find(a => a.value === formData[adAccountName]) || null}
          onChange={(sel) => {
            if (!sel) return;
            setFormData(prev => ({ ...prev, [adAccountName]: sel.value }));
            if (sel.accessToken) setSelectedAccessToken(sel.accessToken);
            if (adAccountName === 'adAccountMeta') setSelectedAdAccount(sel.value);
            setErrors(prev => ({ ...prev, [adAccountName]: false }));
          }}
          className="text-sm"
          styles={selectStyles}
        />
        {errors[adAccountName] && <span className="text-red-500 text-xs">Ad account is required</span>}
      </div>
    </div>
  );

  const platformOptions = [
    { value: 'Facebook', label: 'Facebook', icon: <FaFacebook /> },
    { value: 'Instagram', label: 'Instagram', icon: <FaInstagram /> },
    { value: 'TikTok', label: 'TikTok', icon: <FaTiktok /> },
    { value: 'Google', label: 'Google', icon: <FaGoogle /> },
    { value: 'Snapchat', label: 'Snapchat', icon: <FaSnapchatGhost /> },
    { value: 'LinkedIn', label: 'LinkedIn', icon: <FaLinkedin /> },
    { value: 'Shopify', label: 'Shopify', icon: <FaShopify /> },
  ];

  const objectiveOptions = [
    { value: 'OUTCOME_TRAFFIC', label: 'Traffic', icon: <FaRoad /> },
    { value: 'OUTCOME_ENGAGEMENT', label: 'Engagement', icon: <FaThumbsUp /> },
    { value: 'OUTCOME_APP_PROMOTION', label: 'App Installs', icon: <FaMobileAlt /> },
    { value: 'OUTCOME_AWARENESS', label: 'Awareness / Reach', icon: <FaBroadcastTower /> },
    { value: 'OUTCOME_LEADS', label: 'Lead Generation', icon: <FaClipboardList /> },
    { value: 'OUTCOME_CONVERSIONS', label: 'Conversions', icon: <FaShoppingCart /> },
  ];

  const categoryOptions = [
    { value: 'CREDIT', label: 'Credit', icon: <FaCreditCard /> },
    { value: 'EMPLOYMENT', label: 'Employment', icon: <FaBriefcase /> },
    { value: 'HOUSING', label: 'Housing', icon: <FaHome /> },
    { value: 'ISSUES_ELECTIONS_POLITICS', label: 'Social / Political', icon: <FaBullhorn /> },
  ];

  const bidStrategyOptions = [
    { value: 'LOWEST_COST', label: 'Lowest Cost (Automatic)' },
    { value: 'COST_CAP', label: 'Cost Cap' },
    { value: 'BID_CAP', label: 'Bid Cap' },
    { value: 'TARGET_COST', label: 'Target Cost' },
  ];

  const ctaOptions = [
    { value: 'LEARN_MORE', label: 'Learn More' },
    { value: 'SHOP_NOW', label: 'Shop Now' },
    { value: 'BUY_NOW', label: 'Buy Now' },
    { value: 'SIGN_UP', label: 'Sign Up' },
    { value: 'SUBSCRIBE', label: 'Subscribe' },
    { value: 'WATCH_MORE', label: 'Watch More' },
    { value: 'WATCH_VIDEO', label: 'Watch Video' },
    { value: 'CONTACT_US', label: 'Contact Us' },
    { value: 'GET_QUOTE', label: 'Get Quote' },
    { value: 'APPLY_NOW', label: 'Apply Now' },
    { value: 'BOOK_NOW', label: 'Book Now' },
    { value: 'DOWNLOAD', label: 'Download' },
    { value: 'GET_DIRECTIONS', label: 'Get Directions' },
    { value: 'OPEN_LINK', label: 'Open Link' },
    { value: 'ORDER_NOW', label: 'Order Now' },
    { value: 'MESSAGE_PAGE', label: 'Message Page' },
  ];

  const handleGenerateCreative = async (type) => {
    if (type !== 'animate' && !aiPrompt.trim()) {
      toast.error('Entrez un prompt pour générer le créatif.');
      return;
    }
    if (type === 'animate' && !formData.imageUrl) {
      toast.error('Aucune image source disponible pour l\'animation.');
      return;
    }
    setGenerating(true);
    try {
      const res = await postRequest('/api/creative/generate', {
        type,
        prompt: aiPrompt,
        aspectRatio: aiAspectRatio,
        ...(type === 'animate' && { imageUrl: formData.imageUrl }),
      });
      if (type === 'image') setFormData(p => ({ ...p, imageUrl: res.url }));
      if (type === 'video' || type === 'animate') setFormData(p => ({ ...p, videoUrl: res.url }));
      toast.success('Créatif généré avec succès !');
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || 'Erreur lors de la génération.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {!hideHeader && shopifyProduct && (
        <div className="flex items-center gap-3 mb-4 px-4 py-3 bg-green-50 border border-green-200 rounded-xl">
          {shopifyProduct.image_url && (
            <img src={shopifyProduct.image_url} alt={shopifyProduct.title} className="w-10 h-10 object-cover rounded-lg flex-shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-xs font-semibold text-green-800 leading-tight truncate">
              <FaShopify className="inline mr-1" style={{ color: '#96bf48' }} />
              Pré-rempli depuis Shopify : {shopifyProduct.title}
            </p>
            <p className="text-xs text-green-600 mt-0.5">Titre, texte, image et URL déjà configurés. Vérifiez et ajustez si besoin.</p>
            {aiMode && shopifyProduct.image_url && (
              <button
                type="button"
                onClick={() => handleGenerateCreative('animate')}
                disabled={generating}
                className="mt-1.5 flex items-center gap-1.5 px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-md hover:bg-purple-700 disabled:opacity-50 transition"
              >
                <FaMagic /> {generating ? 'Animation en cours...' : 'Animer ce produit (IA)'}
              </button>
            )}
          </div>
        </div>
      )}
      {!hideHeader && (
        <>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center w-full gap-3">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-[#221C51]">{aiMode ? 'Create Your Ad with AI' : "Let's Create Your Ad"}</h2>
              <p className="text-xs text-gray-500 mt-0.5">{aiMode ? 'L\'assistant à droite remplit le formulaire au fil de la conversation.' : 'Configurez votre campagne sur les plateformes connectées.'}</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center bg-gray-100 rounded-full p-0.5 text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => onSwitchToManual ? onSwitchToManual() : navigate('/dashboard/create-ad')}
                  className={`px-3 py-1 rounded-full transition-all duration-200 ${!aiMode ? 'bg-[#221C51] text-white shadow' : 'text-gray-500 hover:text-[#221C51]'}`}
                >
                  Manual
                </button>
                <button
                  type="button"
                  onClick={() => onSwitchToAI ? onSwitchToAI() : navigate('/dashboard/create-ad-ai')}
                  className={`px-3 py-1 rounded-full transition-all duration-200 ${aiMode ? 'bg-[#221C51] text-white shadow' : 'text-gray-500 hover:text-[#221C51]'}`}
                >
                  Use AI
                </button>
              </div>
              <p className="text-sm text-[#221C51] mr-2 font-semibold">{isSimpleCampaign ? 'Simple' : 'Advanced'}</p>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={!isSimpleCampaign} onChange={handleToggleChange} className="sr-only peer" />
                <div className="w-12 h-6 bg-[#221C51] rounded-full">
                  <div className="w-6 h-6 bg-white rounded-full shadow-md transform peer-checked:translate-x-6 transition-transform duration-200"></div>
                </div>
              </label>
            </div>
          </div>
          <hr className="border-t border-[#221C51]/30 mt-3" />
        </>
      )}

      <div className="max-w-4xl mx-auto mt-4">
        <form className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">

          {/* ── ÉTAPE 1 · CAMPAGNE ── */}
          <div className="col-span-2">
            <StepHeader
              n={1}
              title="Campagne"
              subtitle="Plateformes, objectif, comptes & budget"
              open={showCampaign}
              done={stepDone.campaign}
              onClick={() => openSection('Campaign')}
            />

            {showCampaign && (
              <div className="mt-2 space-y-5">
                <div className="rounded-2xl border border-gray-100 bg-white p-4 md:p-5 shadow-sm">
                  <h4 className="flex items-center gap-2 text-sm font-bold text-[#221C51] mb-4">
                    <FaFlag className="text-[#221C51]/70" /> Détails de la campagne
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#221C51]">Campaign name <span className="text-red-500">*</span></label>
                      <input type="text" name="name" placeholder="Enter Campaign Name" value={formData.name} onChange={handleInputChange}
                        className={`block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100${hl('name')}`} />
                      {errors.name && <span className="text-red-500 text-xs">Name is required</span>}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#221C51]">Campaign Objective</label>
                      <Select name="objective" value={objectiveOptions.find(o => o.value === formData.objective) || null}
                        onChange={handleObjectiveChange}
                        options={objectiveOptions}
                        isOptionDisabled={(o) => objectiveBlockedOn(o.value).length > 0}
                        getOptionLabel={o => {
                          const blocked = objectiveBlockedOn(o.value);
                          return (
                            <div className={`flex items-center ${blocked.length > 0 ? 'opacity-50' : ''}`}>
                              {o.icon}<span className="ml-2">{o.label}</span>
                              {blocked.length > 0 && (
                                <span className="ml-2 text-[10px] text-red-500 font-semibold">✕ indispo sur {blocked.join(', ')}</span>
                              )}
                            </div>
                          );
                        }}
                        className="text-sm" styles={selectStyles} />

                      {/* Matrice objectif × plateforme : montre l'objectif NATIF
                          et le couple optimization goal / billing event que
                          chaque plateforme sélectionnée utilisera réellement. */}
                      {formData.objective && formData.publisherPlatform.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {selectedPlatformKeys(formData.publisherPlatform).map((pk) => {
                            const sup = OBJECTIVE_MATRIX[formData.objective]?.[pk];
                            const del = deliveryFor(pk, formData.objective, { hasPixel: !!formData.pixelId });
                            if (sup && sup.value === null) {
                              return (
                                <span key={pk} className="text-[10px] font-semibold px-2 py-1 rounded-full bg-red-50 text-red-600 border border-red-200">
                                  {pk} : objectif non disponible ✕
                                </span>
                              );
                            }
                            return (
                              <span key={pk} className={`text-[10px] px-2 py-1 rounded-full border ${del?.warning ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}
                                title={del?.warning || ''}>
                                <b className="capitalize">{pk}</b> → {sup?.value || formData.objective}
                                {del && ` · ${del.optimization_goal} / ${del.billing_event}`}
                                {del?.warning ? ' ⚠' : ''}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {!isSimpleCampaign && (
                      <div>
                        <label className="block text-sm font-semibold text-[#221C51]">Category</label>
                        <Select name="category" options={categoryOptions} isMulti
                          value={categoryOptions.filter(o => formData.category.includes(o.value))}
                          onChange={(sel) => handleMultiSelectChange('category', sel)}
                          getOptionLabel={o => <div className="flex items-center">{o.icon}<span className="ml-2">{o.label}</span></div>}
                          className="text-sm" styles={selectStyles} />
                      </div>
                    )}

                    <div className={isSimpleCampaign ? 'md:col-span-2' : ''}>
                      <label className="block text-sm font-semibold text-[#221C51]">Publisher Platform <span className="text-red-500">*</span></label>
                      <Select name="publisherPlatform" options={platformOptions} isMulti
                        value={platformOptions.filter(o => formData.publisherPlatform.includes(o.value))}
                        onChange={handlePlatformsChange}
                        isOptionDisabled={(o) => platformBlockedByObjective(o.value)}
                        getOptionLabel={o => (
                          <div className={`flex items-center ${platformBlockedByObjective(o.value) ? 'opacity-50' : ''}`}>
                            {o.icon}<span className="ml-2">{o.label}</span>
                            {platformBlockedByObjective(o.value) && (
                              <span className="ml-2 text-[10px] text-red-500 font-semibold">✕ objectif non dispo</span>
                            )}
                          </div>
                        )}
                        className="text-sm" styles={selectStyles} />
                      {errors.publisherPlatform && <span className="text-red-500 text-xs">Platform is required</span>}
                    </div>
                  </div>
                </div>

                {(formData.publisherPlatform.includes('Facebook') || formData.publisherPlatform.includes('Instagram')) &&
                  renderBudgetSection('Meta', 'budgetTypeMeta', 'budgetMeta', 'currencyMeta', 'adAccountMeta', metaAdAccounts)}
                {formData.publisherPlatform.includes('TikTok') &&
                  renderBudgetSection('TikTok', 'budgetTypeTikTok', 'budgetTikTok', 'currencyTikTok', 'adAccountTikTok', tiktokAdAccounts)}
                {formData.publisherPlatform.includes('Google') &&
                  renderBudgetSection('Google', 'budgetTypeGoogle', 'budgetGoogle', 'currencyGoogle', 'adAccountGoogle', googleAdAccounts)}
                {formData.publisherPlatform.includes('Snapchat') &&
                  renderBudgetSection('Snapchat', 'budgetTypeSnapchat', 'budgetSnapchat', 'currencySnapchat', 'adAccountSnapchat', snapchatAdAccounts)}
                {formData.publisherPlatform.includes('LinkedIn') &&
                  renderBudgetSection('LinkedIn', 'budgetTypeLinkedIn', 'budgetLinkedIn', 'currencyLinkedIn', 'adAccountLinkedIn', linkedInAdAccounts)}
                {formData.publisherPlatform.includes('Shopify') &&
                  renderBudgetSection('Shopify', 'budgetTypeShopify', 'budgetShopify', 'currencyShopify', 'adAccountShopify', [])}

                {formData.publisherPlatform.length > 0 && (
                  <div className="flex items-center justify-end gap-3 rounded-xl bg-[#221C51]/5 border border-[#221C51]/10 px-4 py-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-[#221C51]">Total Budget</span>
                    <span className="text-lg font-black text-[#221C51]">{Number(formData.totalBudget || 0).toFixed(2)}</span>
                  </div>
                )}

                {/* Passage guidé à l'étape 2 */}
                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    onClick={() => openSection('Adset')}
                    className="px-6 py-2.5 text-sm font-semibold text-white bg-[#221C51] rounded-xl hover:bg-[#2d267a] shadow-sm transition"
                  >
                    Continuer → 2. Audience
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── ÉTAPE 2 · AUDIENCE ── */}
          <div className="col-span-2 mt-2">
            <StepHeader
              n={2}
              title="Audience"
              subtitle="Ciblage, dates & placements"
              open={showAdset}
              done={stepDone.adset}
              onClick={() => openSection('Adset')}
            />

            {showAdset && (
              <div className="mt-4 space-y-5">

                {/* ── Audience Estimate (Meta only) — sticky at top ── */}
                {(formData.publisherPlatform.includes('Facebook') || formData.publisherPlatform.includes('Instagram')) && (() => {
                  const fmtN = (n) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${Math.round(n / 1_000)}K` : String(n || 0);
                  const upper = audienceEstimate?.upperBound || 0;
                  const pct = upper > 100_000_000 ? 100 : upper > 50_000_000 ? 85 : upper > 10_000_000 ? 65 : upper > 2_000_000 ? 45 : upper > 500_000 ? 25 : 10;
                  const clr = upper > 50_000_000 ? 'bg-blue-400' : upper > 10_000_000 ? 'bg-green-500' : upper > 2_000_000 ? 'bg-green-400' : upper > 500_000 ? 'bg-orange-400' : 'bg-red-400';
                  const lbl = upper > 50_000_000 ? 'Très large — idéal notoriété' : upper > 10_000_000 ? 'Large — bon pour la portée' : upper > 2_000_000 ? 'Équilibré — bon ciblage' : upper > 500_000 ? 'Étroit — élargissez le ciblage' : 'Trop étroit — ajustez vos critères';
                  return (
                    <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FaBullseye className="text-[#221C51]" />
                          <span className="text-sm font-bold text-[#221C51] uppercase tracking-wide">Audience estimée — Meta</span>
                        </div>
                        <span className="text-xs text-gray-400">Mis à jour en direct</span>
                      </div>
                      {!selectedAdAccount ? (
                        <p className="text-xs text-gray-500">Sélectionnez un compte publicitaire Meta pour activer l'estimation.</p>
                      ) : audienceEstimate ? (
                        <>
                          <p className="text-3xl font-black text-[#221C51] leading-none">
                            {fmtN(audienceEstimate.lowerBound)} – {fmtN(audienceEstimate.upperBound)}
                            <span className="text-sm font-normal text-gray-500 ml-2">personnes</span>
                          </p>
                          <div className="mt-3 h-2 rounded-full bg-gray-200 overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-500 ${clr}`} style={{ width: `${pct}%` }} />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{lbl}</p>
                        </>
                      ) : (
                        <p className="text-xs text-gray-500">Définissez un pays, un âge et un genre pour voir l'estimation.</p>
                      )}
                    </div>
                  );
                })()}

                {/* ── Identité ── */}
                <div className="rounded-2xl border border-gray-100 bg-white p-4 md:p-5 shadow-sm">
                  <h4 className="flex items-center gap-2 text-sm font-bold text-[#221C51] mb-4">
                    <FaIdCard className="text-[#221C51]/70" /> Identité
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                    <div className="col-span-1 md:col-span-2">
                      <label className="block text-sm font-semibold text-[#221C51]">Ad Set Name <span className="text-red-500">*</span></label>
                      <input type="text" name="adSetName" placeholder="Enter Ad Set Name" value={formData.adSetName || ''} onChange={handleInputChange}
                        className={`block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100${hl('adSetName')}`} />
                      {errors.adSetName && <span className="text-red-500 text-xs">Ad set name is required</span>}
                    </div>

                    <div className="col-span-1">
                      <label className="block text-sm font-semibold text-[#221C51]">Billing Event</label>
                      <select name="billingEvent" value={formData.billingEvent || ''} onChange={handleInputChange}
                        className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100">
                        <option value="IMPRESSIONS">Impressions</option>
                        <option value="LINK_CLICKS">Link Clicks</option>
                        <option value="APP_INSTALLS">App Installs</option>
                        <option value="THRUPLAY">ThruPlay</option>
                      </select>
                    </div>

                    <div className="col-span-1">
                      <label className="block text-sm font-semibold text-[#221C51]">Optimization Goal</label>
                      <select name="optimization_goal" value={formData.optimization_goal || ''} onChange={handleInputChange}
                        className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100">
                        <option value="REACH">Reach</option>
                        <option value="LINK_CLICKS">Link Clicks</option>
                        <option value="IMPRESSIONS">Impressions</option>
                        <option value="LEAD_GENERATION">Lead Generation</option>
                        <option value="APP_INSTALLS">App Installs</option>
                        <option value="OFFSITE_CONVERSIONS">Offsite Conversions</option>
                        <option value="THRUPLAY">ThruPlay</option>
                      </select>
                    </div>

                    {formData.publisherPlatform.length > 0 && !isSimpleCampaign && (
                      <div className="col-span-1">
                        <label className="block text-sm font-semibold text-[#221C51]">Bid Strategy</label>
                        <Select name="bid_strategy"
                          value={bidStrategyOptions.find(o => o.value === formData.bid_strategy)}
                          onChange={(sel) => handleInputChange({ target: { name: 'bid_strategy', value: sel.value } })}
                          options={bidStrategyOptions} styles={selectStyles} className="text-sm" />
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Géographie ── */}
                <div className="rounded-2xl border border-gray-100 bg-white p-4 md:p-5 shadow-sm">
                  <h4 className="flex items-center gap-2 text-sm font-bold text-[#221C51] mb-4">
                    <FaMapMarkerAlt className="text-[#221C51]/70" /> Géographie
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                    <div className="col-span-1">
                      <label className="block text-sm font-semibold text-[#221C51]">Country</label>
                      <Select name="location" isMulti
                        options={locationOptions}
                        value={locationOptions.flatMap(g => g.options).filter(o => formData.location?.includes(o.value))}
                        onChange={(sel) => {
                          handleSelectChange('location', sel);
                          if (!sel?.some(o => o.value === 'MA')) {
                            setFormData(prev => ({ ...prev, cities: [] }));
                          }
                        }}
                        placeholder="Select countries..."
                        className="text-sm" styles={selectStyles} />
                    </div>

                    {formData.location?.includes('MA') && (
                      <div className="col-span-1">
                        <label className="block text-sm font-semibold text-[#221C51]">
                          Morocco Cities <span className="text-xs font-normal text-gray-400">(optional)</span>
                        </label>
                        <Select name="cities" isMulti
                          options={moroccoCitiesOptions}
                          value={moroccoCitiesOptions.filter(o => formData.cities?.includes(o.value))}
                          onChange={(sel) => handleSelectChange('cities', sel)}
                          placeholder="All of Morocco..."
                          className="text-sm" styles={selectStyles} />
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Démographie ── */}
                <div className="rounded-2xl border border-gray-100 bg-white p-4 md:p-5 shadow-sm">
                  <h4 className="flex items-center gap-2 text-sm font-bold text-[#221C51] mb-4">
                    <FaUsers className="text-[#221C51]/70" /> Démographie
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                    <div className="col-span-1">
                      <label className="block text-sm font-semibold text-[#221C51]">Age</label>
                      <Select name="age" isMulti
                        options={[
                          { value: '18-24', label: '18-24' }, { value: '25-34', label: '25-34' },
                          { value: '35-44', label: '35-44' }, { value: '45-54', label: '45-54' },
                          { value: '55-65', label: '55-65' }, { value: 'all', label: 'All' },
                        ]}
                        value={formData.age?.map(a => ({ value: a, label: a }))}
                        onChange={(sel) => handleSelectChange('age', sel)}
                        className="text-sm" styles={selectStyles} />
                    </div>

                    <div className="col-span-1">
                      <label className="block text-sm font-semibold text-[#221C51]">Gender</label>
                      <div className="join w-full">
                        {['All', 'Male', 'Female'].map(g => (
                          <button key={g} type="button"
                            className={`join-item btn btn-sm flex-1 font-medium border-[#d1d5db] ${formData.gender === g ? 'bg-[#221C51] hover:bg-[#221C51] text-white border-[#221C51]' : 'bg-white text-[#221C51]'}`}
                            onClick={() => setFormData({ ...formData, gender: g })}>{g}</button>
                        ))}
                      </div>
                    </div>

                    {!isSimpleCampaign && (
                      <div className="col-span-1 md:col-span-2">
                        <label className="block text-sm font-semibold text-[#221C51]">
                          Languages <span className="text-xs font-normal text-gray-400">(optional — leave empty for all)</span>
                        </label>
                        <Select name="languages" isMulti
                          options={languageOptions}
                          value={languageOptions.filter(o => formData.languages?.includes(o.value))}
                          onChange={(sel) => handleSelectChange('languages', sel)}
                          placeholder="All languages..."
                          className="text-sm" styles={selectStyles} />
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Centres d'intérêt & placements ── */}
                {!isSimpleCampaign && (formData.publisherPlatform.includes('Facebook') || formData.publisherPlatform.includes('Instagram') || formData.publisherPlatform.includes('TikTok')) && (
                  <div className="rounded-2xl border border-gray-100 bg-white p-4 md:p-5 shadow-sm">
                    <h4 className="flex items-center gap-2 text-sm font-bold text-[#221C51] mb-4">
                      <FaFilter className="text-[#221C51]/70" /> Centres d'intérêt &amp; placements
                    </h4>
                    <div className="space-y-4">

                      {/* ── Interests ─────────────────────────────────────── */}
                      {(formData.publisherPlatform.includes('Facebook') || formData.publisherPlatform.includes('Instagram')) && (
                        <div>
                          <label className="block text-sm font-semibold text-[#221C51] mb-1">
                            <FaHeart className="inline mr-1 text-pink-500" />
                            Interests <span className="text-xs font-normal text-gray-400">(Meta — type to search)</span>
                          </label>
                          <AsyncSelect
                            isMulti
                            cacheOptions
                            loadOptions={loadMetaInterests}
                            value={(formData.interests || []).map(i => ({ value: i.id, label: i.name, id: i.id, name: i.name }))}
                            onChange={(sel) => setFormData(prev => ({
                              ...prev,
                              interests: (sel || []).map(o => ({ id: o.id || o.value, name: o.name || o.label })),
                            }))}
                            placeholder="Search interests (e.g. Football, Travel, Fashion)..."
                            noOptionsMessage={({ inputValue }) => inputValue.length < 2 ? 'Type at least 2 characters...' : 'No interests found'}
                            className="text-sm"
                            styles={selectStyles}
                          />
                        </div>
                      )}

                      {formData.publisherPlatform.includes('TikTok') && (
                        <div>
                          <label className="block text-sm font-semibold text-[#221C51] mb-1">
                            <FaHeart className="inline mr-1 text-pink-500" />
                            Interests <span className="text-xs font-normal text-gray-400">(TikTok — select categories)</span>
                          </label>
                          <Select
                            isMulti
                            options={tiktokInterestCategories}
                            value={tiktokInterestCategories.filter(o => (formData.interests || []).some(i => i.id === o.value))}
                            onChange={(sel) => setFormData(prev => ({
                              ...prev,
                              interests: (sel || []).map(o => ({ id: o.value, name: o.label })),
                            }))}
                            placeholder="Select TikTok interest categories..."
                            className="text-sm"
                            styles={selectStyles}
                          />
                        </div>
                      )}

                      {/* ── TikTok Identity ──────────────────────────────── */}
                      {formData.publisherPlatform.includes('TikTok') && tiktokIdentities.length > 0 && (
                        <div>
                          <label className="block text-sm font-semibold text-[#221C51] mb-1">
                            TikTok Identity <span className="text-xs font-normal text-gray-400">(compte affiché comme source de l'annonce)</span>
                          </label>
                          <Select name="identityId"
                            value={tiktokIdentities
                              .map(i => ({ value: i.identityId, label: i.displayName, identityType: i.identityType }))
                              .find(o => o.value === formData.identityId)}
                            onChange={(sel) => setFormData(prev => ({ ...prev, identityId: sel.value, identityType: sel.identityType }))}
                            options={tiktokIdentities.map(i => ({ value: i.identityId, label: i.displayName, identityType: i.identityType }))}
                            styles={selectStyles} className="text-sm" />
                        </div>
                      )}

                      {/* ── TikTok Post existant (Spark Ads) ────────────────
                          Requis pour la plupart des objectifs vidéo — confirmé en
                          live : sans post existant, TikTok rejette avec "The
                          source of this post is invalid." Choix dans la liste OU
                          item ID collé manuellement (post d'un autre compte
                          autorisé, etc.). */}
                      {formData.publisherPlatform.includes('TikTok') && formData.creativeType === 'Video' && (
                        <div>
                          <label className="block text-sm font-semibold text-[#221C51] mb-1">
                            TikTok Post <span className="text-xs font-normal text-gray-400">(publication existante — recommandé pour la plupart des objectifs)</span>
                          </label>
                          {tiktokVideos.length > 0 && (
                            <Select name="tiktokItemId"
                              value={tiktokVideos.map(v => ({ value: v.itemId, label: v.text || `Post ${v.itemId} (${v.duration?.toFixed(0) || '?'}s)` })).find(o => o.value === formData.tiktokItemId)}
                              onChange={(sel) => setFormData(prev => ({ ...prev, tiktokItemId: sel?.value || '' }))}
                              options={tiktokVideos.map(v => ({ value: v.itemId, label: v.text || `Post ${v.itemId} (${v.duration?.toFixed(0) || '?'}s)` }))}
                              isClearable
                              placeholder="Choisir une publication…"
                              styles={selectStyles} className="text-sm mb-2" />
                          )}
                          <input type="text" name="tiktokItemId" placeholder="Ou collez un item ID TikTok directement"
                            value={formData.tiktokItemId || ''} onChange={handleInputChange}
                            className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                          {tiktokVideos.length === 0 && (
                            <p className="text-xs text-gray-400 mt-1">Aucune publication trouvée sur le compte TikTok connecté.</p>
                          )}
                        </div>
                      )}

                      {/* ── TikTok Pixel (requis pour Ventes/Conversions) ──── */}
                      {formData.objective === 'OUTCOME_CONVERSIONS' && formData.publisherPlatform.includes('TikTok') && (
                        <div>
                          <label className="block text-sm font-semibold text-[#221C51] mb-1">TikTok Pixel <span className="text-red-500">*</span></label>
                          {tiktokPixels.length === 0 ? (
                            <p className="text-xs text-amber-600">
                              Aucun pixel TikTok trouvé. <a href="/dashboard/pixels" target="_blank" rel="noreferrer" className="underline font-semibold">Créez-en un</a> avant de lancer cette campagne.
                            </p>
                          ) : (
                            <Select name="pixelId"
                              value={tiktokPixels.map(p => ({ value: p.pixelId, label: p.name || p.pixelId })).find(o => o.value === formData.pixelId)}
                              onChange={(sel) => setFormData(prev => ({ ...prev, pixelId: sel.value }))}
                              options={tiktokPixels.map(p => ({ value: p.pixelId, label: p.name || p.pixelId }))}
                              styles={selectStyles} className="text-sm" />
                          )}
                        </div>
                      )}

                      {/* ── TikTok App (requis pour Promotion d'appli) ──────
                          Aucune API TikTok ne permet de créer une app depuis
                          zéro (comme pour les Pages Meta) — seulement lister
                          celles déjà enregistrées, d'où le fallback "coller l'ID". */}
                      {formData.objective === 'OUTCOME_APP_PROMOTION' && formData.publisherPlatform.includes('TikTok') && (
                        <div>
                          <label className="block text-sm font-semibold text-[#221C51] mb-1">TikTok App <span className="text-red-500">*</span></label>
                          {tiktokApps.length > 0 && (
                            <Select name="tiktokAppId"
                              value={tiktokApps.map(a => ({ value: a.appId, label: a.name })).find(o => o.value === formData.tiktokAppId)}
                              onChange={(sel) => setFormData(prev => ({ ...prev, tiktokAppId: sel?.value || '' }))}
                              options={tiktokApps.map(a => ({ value: a.appId, label: a.name }))}
                              isClearable styles={selectStyles} className="text-sm mb-2" />
                          )}
                          <input type="text" name="tiktokAppId" placeholder="Ou collez un App ID TikTok Ads"
                            value={formData.tiktokAppId || ''} onChange={handleInputChange}
                            className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                          {tiktokApps.length === 0 && (
                            <p className="text-xs text-amber-600 mt-1">
                              Aucune app enregistrée sur ce compte TikTok Ads. Enregistrez-en une dans TikTok Ads Manager → Assets → App avant de lancer cette campagne.
                            </p>
                          )}
                        </div>
                      )}

                      {/* ── TikTok Placement ─────────────────────────────── */}
                      {formData.publisherPlatform.includes('TikTok') && (
                        <div>
                          <label className="block text-sm font-semibold text-[#221C51] mb-1">TikTok Placement</label>
                          <Select name="tiktokPlacement"
                            value={[
                              { value: 'PLACEMENT_TYPE_AUTOMATIC', label: 'Automatic (Recommended)' },
                              { value: 'PLACEMENT_TYPE_NORMAL', label: 'Manual — TikTok In-Feed' },
                            ].find(o => o.value === (formData.tiktokPlacement || 'PLACEMENT_TYPE_AUTOMATIC'))}
                            onChange={(sel) => handleInputChange({ target: { name: 'tiktokPlacement', value: sel.value } })}
                            options={[
                              { value: 'PLACEMENT_TYPE_AUTOMATIC', label: 'Automatic (Recommended)' },
                              { value: 'PLACEMENT_TYPE_NORMAL', label: 'Manual — TikTok In-Feed' },
                            ]} styles={selectStyles} className="text-sm" />
                        </div>
                      )}

                      {/* ── Meta Placements ─────────────────────────────── */}
                      {(formData.publisherPlatform.includes('Facebook') || formData.publisherPlatform.includes('Instagram')) && (
                        <div>
                          <label className="block text-sm font-semibold text-[#221C51] mb-1">
                            Placements <span className="text-xs font-normal text-gray-400">(leave empty for Automatic Placements)</span>
                          </label>
                          <Select
                            isMulti
                            options={metaPlacementOptions}
                            value={metaPlacementOptions.flatMap(g => g.options).filter(o => (formData.placements || []).includes(o.value))}
                            onChange={(sel) => setFormData(prev => ({ ...prev, placements: (sel || []).map(o => o.value) }))}
                            placeholder="Automatic Placements (recommended)..."
                            className="text-sm"
                            styles={selectStyles}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── LinkedIn-specific targeting ──────────────────── */}
                {!isSimpleCampaign && formData.publisherPlatform.includes('LinkedIn') && (
                  <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 md:p-5 shadow-sm">
                    <h4 className="text-sm font-bold text-[#221C51] mb-4 flex items-center gap-2">
                      <FaLinkedin className="text-[#0077b5]" /> LinkedIn Professional Targeting
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#221C51] mb-1">Seniority</label>
                        <Select isMulti options={linkedinSeniorityOptions}
                          value={linkedinSeniorityOptions.filter(o => (formData.linkedinSeniorities || []).includes(o.value))}
                          onChange={(sel) => setFormData(prev => ({ ...prev, linkedinSeniorities: (sel || []).map(o => o.value) }))}
                          placeholder="All seniorities..." className="text-sm" styles={selectStyles} />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#221C51] mb-1">Industry</label>
                        <Select isMulti options={linkedinIndustryOptions}
                          value={linkedinIndustryOptions.filter(o => (formData.linkedinIndustries || []).includes(o.value))}
                          onChange={(sel) => setFormData(prev => ({ ...prev, linkedinIndustries: (sel || []).map(o => o.value) }))}
                          placeholder="All industries..." className="text-sm" styles={selectStyles} />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#221C51] mb-1">Job Function</label>
                        <Select isMulti options={linkedinJobFunctionOptions}
                          value={linkedinJobFunctionOptions.filter(o => (formData.linkedinJobFunctions || []).includes(o.value))}
                          onChange={(sel) => setFormData(prev => ({ ...prev, linkedinJobFunctions: (sel || []).map(o => o.value) }))}
                          placeholder="All job functions..." className="text-sm" styles={selectStyles} />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#221C51] mb-1">Company Size</label>
                        <Select isMulti options={linkedinCompanySizeOptions}
                          value={linkedinCompanySizeOptions.filter(o => (formData.linkedinCompanySizes || []).includes(o.value))}
                          onChange={(sel) => setFormData(prev => ({ ...prev, linkedinCompanySizes: (sel || []).map(o => o.value) }))}
                          placeholder="All company sizes..." className="text-sm" styles={selectStyles} />
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Calendrier & fréquence ── */}
                <div className="rounded-2xl border border-gray-100 bg-white p-4 md:p-5 shadow-sm">
                  <h4 className="flex items-center gap-2 text-sm font-bold text-[#221C51] mb-4">
                    <FaCalendarAlt className="text-[#221C51]/70" /> Calendrier
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                    <div className="col-span-1">
                      <label className="block text-sm font-semibold text-[#221C51]">Start Time <span className="text-red-500">*</span></label>
                      <input type="datetime-local" name="startTime" value={formData.startTime || ''} onChange={handleInputChange}
                        className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                      {errors.startTime && <span className="text-red-500 text-xs">Start time is required</span>}
                    </div>

                    {!isSimpleCampaign && (
                      <div className="col-span-1">
                        <label className="block text-sm font-semibold text-[#221C51]">End Time</label>
                        <input type="datetime-local" name="endTime" value={formData.endTime || ''} onChange={handleInputChange}
                          className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                      </div>
                    )}
                  </div>

                  {!isSimpleCampaign && (
                    <div className="mt-5 pt-4 border-t border-gray-100">
                      <h5 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500 mb-3">
                        <FaClock className="text-gray-400" /> Frequency Cap
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-[#221C51]">Frequency Cap</label>
                          <input type="number" name="frequency_cap" min="0"
                            value={formData.frequency_cap} onChange={handleInputChange}
                            placeholder="e.g. 3" className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                          <p className="text-xs text-gray-400 mt-0.5">Max impressions per user</p>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#221C51]">Reset Every</label>
                          <input type="number" name="frequency_cap_reset_period" min="1"
                            value={formData.frequency_cap_reset_period} onChange={handleInputChange}
                            placeholder="e.g. 7" className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#221C51]">Period</label>
                          <Select name="frequency_cap_time_unit"
                            value={[{value:'DAY',label:'Days'},{value:'WEEK',label:'Weeks'},{value:'MONTH',label:'Months'}].find(o => o.value === formData.frequency_cap_time_unit)}
                            onChange={(sel) => handleInputChange({ target: { name: 'frequency_cap_time_unit', value: sel.value } })}
                            options={[{value:'DAY',label:'Days'},{value:'WEEK',label:'Weeks'},{value:'MONTH',label:'Months'}]}
                            styles={selectStyles} className="text-sm" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Promoted Object fields (dynamic) ── */}
                {promotedObjectFields.length > 0 && (
                  <div className="rounded-2xl border border-gray-100 bg-white p-4 md:p-5 shadow-sm">
                    <h4 className="flex items-center gap-2 text-sm font-bold text-[#221C51] mb-4">
                      <FaBoxOpen className="text-[#221C51]/70" /> Promoted Object
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                      {promotedObjectFields.map((field, i) => (
                        <div key={i} className="col-span-1">
                          <label className="block text-sm font-semibold text-[#221C51]">{field.name} {field.platform && `(${field.platform})`}</label>
                          {field.type === 'url' && <input type="url" name={field.name} placeholder={`Enter ${field.name}`} value={formData[field.name] || ''} onChange={handleInputChange} className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />}
                          {field.type === 'text' && <input type="text" name={field.name} placeholder={`Enter ${field.name}`} value={formData[field.name] || ''} onChange={handleInputChange} className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />}
                          {field.type === 'select' && <Select name={field.name} options={field.options} value={field.options.find(o => o.value === formData[field.name])} onChange={(sel) => handleSelectPromtedObjectChange(field.name, sel)} className="text-sm" styles={selectStyles} />}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Passage guidé à l'étape 3 */}
                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    onClick={() => openSection('Ad')}
                    className="px-6 py-2.5 text-sm font-semibold text-white bg-[#221C51] rounded-xl hover:bg-[#2d267a] shadow-sm transition"
                  >
                    Continuer → 3. Créatif
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── ÉTAPE 3 · CRÉATIF & LANCEMENT ── */}
          <div className="col-span-2 mt-2">
            <StepHeader
              n={3}
              title="Créatif & lancement"
              subtitle="Visuel, textes, aperçu de la pub"
              open={showAd}
              done={stepDone.ad}
              onClick={() => openSection('Ad')}
            />

            {showAd && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 rounded-2xl border border-gray-100 bg-white p-4 md:p-5 shadow-sm space-y-4">
                  <h4 className="flex items-center gap-2 text-sm font-bold text-[#221C51] mb-1">
                    <FaPenNib className="text-[#221C51]/70" /> Créative
                  </h4>
                  <div>
                    <label className="block text-sm font-semibold text-[#221C51]">AdCreative Name <span className="text-red-500">*</span></label>
                    <input type="text" name="adCreativeName" placeholder="Enter AdCreative name" value={formData.adCreativeName} onChange={handleInputChange}
                      className={`block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100${hl('adCreativeName')}`} />
                    {errors.adCreativeName && <span className="text-red-500 text-xs">Creative name is required</span>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#221C51]">Format</label>
                    <div className="join w-full">
                      {['Image', 'Video'].map(fmt => (
                        <button key={fmt} type="button"
                          className={`join-item btn btn-sm flex-1 font-medium border-[#d1d5db] ${formData.creativeType === fmt ? 'bg-[#221C51] hover:bg-[#221C51] text-white border-[#221C51]' : 'bg-white text-[#221C51]'}`}
                          onClick={() => setFormData({ ...formData, creativeType: fmt, videoUrl: '', videoThumbnailUrl: '', ...(fmt === 'Video' ? { imageUrl: '' } : {}) })}>
                          {fmt}
                        </button>
                      ))}
                    </div>
                  </div>

                  {formData.creativeType === 'Image' && (
                    <div>
                      <label className="block text-sm font-semibold text-[#221C51]">Image <span className="text-red-500">*</span></label>
                      <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'image')}
                        className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                      {errors.imageFile && <span className="text-red-500 text-xs">Image is required</span>}
                      {formData.imageUrl && <img src={formData.imageUrl} alt="preview" className="mt-2 h-24 rounded" />}

                      {/* ── AI Image Generation — only in Create with AI ── */}
                      {aiMode && (
                        <div className="mt-3 p-3 bg-[#f4f3ff] border border-[#c5bff7] rounded-lg">
                          <p className="text-xs font-bold text-[#221C51] mb-2 flex items-center gap-1">
                            <FaMagic className="text-purple-500" /> Générer avec l'IA
                          </p>
                          <input
                            type="text"
                            placeholder="Ex : produit cosmétique fond blanc épuré, lumière studio"
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            className="block w-full p-2 text-xs rounded-md border border-[#c5bff7] bg-white mb-2"
                          />
                          <div className="flex flex-col md:flex-row gap-2 md:items-center">
                            <select
                              value={aiAspectRatio}
                              onChange={(e) => setAiAspectRatio(e.target.value)}
                              className="text-xs border border-[#c5bff7] rounded-md p-1.5 bg-white"
                            >
                              <option value="1:1">1:1 (carré)</option>
                              <option value="4:5">4:5 (portrait)</option>
                              <option value="16:9">16:9 (paysage)</option>
                              <option value="9:16">9:16 (vertical)</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => handleGenerateCreative('image')}
                              disabled={generating}
                              className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#221C51] text-white text-xs font-semibold rounded-md hover:bg-[#2d267a] disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                              <FaMagic /> {generating ? 'Génération...' : 'Générer Image'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {formData.creativeType === 'Video' && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-[#221C51]">Video <span className="text-red-500">*</span></label>
                          <input type="file" accept="video/*" onChange={(e) => handleFileUpload(e, 'video')}
                            className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                          {errors.videoFile && <span className="text-red-500 text-xs">Video is required</span>}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#221C51]">Thumbnail <span className="text-red-500">*</span></label>
                          <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'videoThumbnail')}
                            className="block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100" />
                        </div>
                      </div>

                      {/* ── AI Video Generation — only in Create with AI ── */}
                      {aiMode && (
                        <div className="p-3 bg-[#f4f3ff] border border-[#c5bff7] rounded-lg">
                          <p className="text-xs font-bold text-[#221C51] mb-2 flex items-center gap-1">
                            <FaMagic className="text-purple-500" /> Générer une vidéo avec l'IA
                          </p>
                          <input
                            type="text"
                            placeholder="Ex : produit en mouvement, fond épuré, lumière cinématique"
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            className="block w-full p-2 text-xs rounded-md border border-[#c5bff7] bg-white mb-2"
                          />
                          <div className="flex flex-col md:flex-row gap-2 md:items-center">
                            <select
                              value={aiAspectRatio}
                              onChange={(e) => setAiAspectRatio(e.target.value)}
                              className="text-xs border border-[#c5bff7] rounded-md p-1.5 bg-white"
                            >
                              <option value="9:16">9:16 (TikTok / Reels)</option>
                              <option value="1:1">1:1 (carré)</option>
                              <option value="16:9">16:9 (YouTube)</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => handleGenerateCreative('video')}
                              disabled={generating}
                              className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#221C51] text-white text-xs font-semibold rounded-md hover:bg-[#2d267a] disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                              <FaMagic /> {generating ? 'Génération...' : 'Générer Vidéo'}
                            </button>
                          </div>
                          <p className="text-xs text-gray-400 mt-1.5">Durée : 5 sec · Powered by Kling 3.0</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-[#221C51]">Ad Title <span className="text-red-500">*</span></label>
                    <input type="text" name="adTitle" placeholder="Enter ad title" value={formData.adTitle} onChange={handleInputChange}
                      className={`block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100${hl('adTitle')}`} />
                    {errors.adTitle && <span className="text-red-500 text-xs">Title is required</span>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#221C51]">Main Ad Text <span className="text-red-500">*</span></label>
                    <textarea name="adText" placeholder="Enter main ad text" value={formData.adText} onChange={handleInputChange} rows={3}
                      className={`block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100${hl('adText')}`} />
                    {errors.adText && <span className="text-red-500 text-xs">Ad text is required</span>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#221C51]">Description <span className="text-red-500">*</span></label>
                    <textarea name="description" placeholder="Enter description" value={formData.description} onChange={handleInputChange} rows={3}
                      className={`block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100${hl('description')}`} />
                    {errors.description && <span className="text-red-500 text-xs">Description is required</span>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#221C51]">Call to Action <span className="text-red-500">*</span></label>
                    <Select name="callToAction" options={ctaOptions}
                      value={ctaOptions.find(o => o.value === formData.callToAction) || null}
                      onChange={(sel) => handleInputChange({ target: { name: 'callToAction', value: sel.value } })}
                      className="text-sm" styles={selectStyles} />
                    {errors.callToAction && <span className="text-red-500 text-xs">Call to action is required</span>}

                    {/* Matrice CTA × plateforme : toutes les APIs n'ont pas tous
                        les boutons — on montre le bouton NATIF que chaque
                        plateforme sélectionnée affichera réellement (⇢ =
                        équivalent le plus proche, converti automatiquement). */}
                    {formData.callToAction && formData.publisherPlatform.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {selectedPlatformKeys(formData.publisherPlatform).filter(pk => pk !== 'google').map((pk) => {
                          const s = CTA_MATRIX[formData.callToAction]?.[pk];
                          if (!s) return null;
                          return (
                            <span key={pk} className={`text-[10px] px-2 py-1 rounded-full border ${s.exact ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}
                              title={s.exact ? 'CTA disponible tel quel' : 'CTA indisponible sur cette plateforme — converti automatiquement'}>
                              <b className="capitalize">{pk}</b> {s.exact ? '✓' : `⇢ ${s.value}`}
                            </span>
                          );
                        })}
                        {selectedPlatformKeys(formData.publisherPlatform).includes('google') && (
                          <span className="text-[10px] px-2 py-1 rounded-full border bg-gray-50 text-gray-500 border-gray-200">
                            google : CTA automatique (RSA)
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#221C51]">Destination URL <span className="text-red-500">*</span></label>
                    <input type="text" name="destinationUrl" placeholder="https://example.com" value={formData.destinationUrl} onChange={handleInputChange}
                      className={`block w-full p-2 text-sm rounded-md border border-gray-300 bg-gray-100${hl('destinationUrl')}`} />
                    {errors.destinationUrl && <span className="text-red-500 text-xs">Destination URL is required</span>}
                  </div>
                </div>

                {/* Ad Preview */}
                <div className="lg:col-span-1">
                  <div className="rounded-2xl border border-gray-100 bg-white p-4 md:p-5 shadow-sm lg:sticky lg:top-6">
                    <h4 className="flex items-center gap-2 text-sm font-bold text-[#221C51] mb-3">
                      <FaEye className="text-[#221C51]/70" /> Aperçu
                    </h4>
                    {formData.imageUrl
                      ? <img src={formData.imageUrl} alt="Ad" className="w-full h-48 object-cover rounded-xl mb-2" />
                      : <div className="w-full h-48 bg-gray-100 rounded-xl flex items-center justify-center mb-2"><FaRegImage className="text-gray-400 text-4xl" /></div>
                    }
                    <p className="text-lg font-bold text-[#221C51]">{formData.adTitle || 'Your ad title'}</p>
                    <p className="text-sm text-[#221C51] mt-1">{formData.adText || 'Main ad text appears here.'}</p>
                    <p className="text-xs text-gray-500 mt-1">{formData.description || 'Description appears here.'}</p>
                    {/* Aperçu uniquement — pas un vrai lien, ne doit pas être cliquable */}
                    <div className="block w-full text-center px-4 py-2 mt-3 text-sm text-white bg-[#221C51] rounded-md cursor-default select-none">
                      {ctaOptions.find(o => o.value === formData.callToAction)?.label || 'Call to Action'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── SUBMIT ── */}
          <div className="col-span-1 md:col-span-2 flex justify-end mt-6 pt-4 border-t border-gray-200">
            <button type="button" onClick={handleSubmit} disabled={submitting}
              className="btn btn-md w-full md:w-auto px-8 text-white bg-[#221C51] hover:bg-[#2d267a] border-none shadow-md disabled:opacity-50 font-semibold text-sm">
              {submitting ? 'Création en cours...' : 'Lancer la campagne'}
            </button>
          </div>
        </form>
      </div>
      <ToastContainer />
    </div>
  );
};

export default CreateAd;
